import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { investorAccountAPI, fundAPI, transactionTypesAPI } from '../services/api';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { ArrowLeft, Edit, Plus, Trash2, Save, X, Upload, Download, FolderPlus, FileText, ChevronDown, ChevronRight, ChevronUp, Loader2, History, ArrowLeftRight } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import BulkUploadModal from '../components/BulkUploadModal';
import DrawdownCallsSection from '../components/investors/DrawdownCallsSection';
import BankDematTab from '../components/investors/BankDematTab';
import NominationTab from '../components/investors/NominationTab';
import ComplianceTab from '../components/investors/ComplianceTab';
import SectionChangeHistory from '../components/investors/SectionChangeHistory';
import { EntityAuditHistory } from './AuditTrail';
import { useDateFormat } from '../hooks/useDateFormat';
import { useAuth } from '../contexts/AuthContext';

const txnTypeDefaults = ['Commitment Agreed', 'Drawdown Received', 'Distribution', 'Advance Drawdown Received'];

const InvestorAccountDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canEdit, canDelete } = usePermissions() || {};
  const fmtDate = useDateFormat();
  const [account, setAccount] = useState(null);
  const [investments, setInvestments] = useState([]);
  // Transfer History modal state — powered by
  // GET /api/investor-accounts/{account_id}/transfer-history?fund_id=…
  // Timeline surfaces every committed transfer this account had (in/out)
  // scoped to the clicked fund, giving auditors the lineage they need.
  const [historyModal, setHistoryModal] = useState(null); // { fundId, fundName }
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (fundId, fundName) => {
    setHistoryModal({ fundId, fundName });
    setHistoryItems([]);
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/investor-accounts/${id}/transfer-history?fund_id=${encodeURIComponent(fundId)}`,
      );
      setHistoryItems(data?.items || []);
    } catch {
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  };
  const [transactions, setTransactions] = useState([]);
  const [navs, setNavs] = useState([]);
  const [funds, setFunds] = useState([]);
  const [txnTypes, setTxnTypes] = useState(txnTypeDefaults);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [fundFilter, setFundFilter] = useState('');
  const [fundClassesMap, setFundClassesMap] = useState({});
  const [newTxnType, setNewTxnType] = useState('');
  const [showNewTxnType, setShowNewTxnType] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);

  // Investment Summary controls
  const [currencyFormat, setCurrencyFormat] = useState('full');
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(83.5);

  // Documentation state
  const [docSections, setDocSections] = useState([]);
  const [pledgedSummary, setPledgedSummary] = useState(null);
  const [showPledgedPopup, setShowPledgedPopup] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  // Tranche expansion state per (investment_id + class_name) — controls the
  // collapsible ledger row under each Investment Summary class line.
  const [expandedTranches, setExpandedTranches] = useState({});
  const [newSectionName, setNewSectionName] = useState('');
  const [showNewSection, setShowNewSection] = useState(false);
  const [newItemName, setNewItemName] = useState({});
  const [showNewItem, setShowNewItem] = useState({});
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  // Forms
  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm] = useState({ fund_id: '', ppm_no: '', class_of_units: [], commitment_amount: 0 });
  const [editingInv, setEditingInv] = useState(null);
  const [showTxnForm, setShowTxnForm] = useState(false);
  const [txnForm, setTxnForm] = useState({ fund_id: '', date_of_transaction: '', transaction_type: 'Drawdown Received', transaction_amount: 0, class_of_units: '', isin: '', is_pledged: false, remarks: '' });
  const [showNavForm, setShowNavForm] = useState(false);
  const [navForm, setNavForm] = useState({ fund_id: '', date_of_nav: '', nav_value: 0 });
  // Class-wise NAV override editor (Phase N)
  const [editingNav, setEditingNav] = useState(null); // the account_nav row being edited
  const [navOverrideClasses, setNavOverrideClasses] = useState([]); // [{class_name, units, nav_per_unit, overridden}]
  const [navOverrideSaving, setNavOverrideSaving] = useState(false);
  const [expandedNavId, setExpandedNavId] = useState(null);

  // Bulk upload modals
  const [showBulkTxn, setShowBulkTxn] = useState(false);
  const [showBulkNav, setShowBulkNav] = useState(false);

  // SOA Generation modal state
  const [showSOAModal, setShowSOAModal] = useState(false);
  const [soaFundId, setSOAFundId] = useState('');
  const [soaTemplates, setSOATemplates] = useState([]);
  const [soaTemplateId, setSOATemplateId] = useState('');
  const [soaGenerating, setSOAGenerating] = useState(false);
  const [soaFromDate, setSOAFromDate] = useState('since_inception');
  const [soaToDate, setSOAToDate] = useState('');
  const [soaIncludeNav, setSOAIncludeNav] = useState(true);

  // Transaction multi-select and sort
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [txnSortDir, setTxnSortDir] = useState('desc');

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [accRes, invRes, txnRes, navRes, fundRes, txnTypesRes, docRes] = await Promise.all([
        investorAccountAPI.getById(id),
        investorAccountAPI.getInvestments(id),
        investorAccountAPI.getTransactions(id),
        investorAccountAPI.getNAVs(id),
        fundAPI.getAll(),
        transactionTypesAPI.get().catch(() => ({ data: { types: txnTypeDefaults } })),
        investorAccountAPI.getDocSections(id).catch(() => ({ data: [] })),
      ]);
      setAccount(accRes.data);
      setInvestments(invRes.data);
      setTransactions(txnRes.data);
      setNavs(navRes.data);
      setFunds(fundRes.data);
      setTxnTypes(txnTypesRes.data.types || txnTypeDefaults);
      setDocSections(docRes.data);
      const expanded = {};
      (docRes.data || []).forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
      // Check pledged status
      try {
        const pledgedRes = await investorAccountAPI.getPledgedSummary(id);
        setPledgedSummary(pledgedRes.data);
        if (pledgedRes.data.has_pledged) setShowPledgedPopup(true);
      } catch {}
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getFund = (fundId) => funds.find(f => f.id === fundId);
  const investmentFundIds = investments.map(inv => inv.fund_id);
  const investmentFunds = funds.filter(f => investmentFundIds.includes(f.id));

  useEffect(() => {
    const loadFundClasses = async () => {
      const classMap = {};
      for (const fundId of investmentFundIds) {
        if (!fundClassesMap[fundId]) {
          try {
            const res = await fundAPI.getClasses(fundId);
            classMap[fundId] = res.data || [];
          } catch { classMap[fundId] = []; }
        }
      }
      if (Object.keys(classMap).length > 0) {
        setFundClassesMap(prev => ({ ...prev, ...classMap }));
      }
    };
    if (investmentFundIds.length > 0) loadFundClasses();
  }, [investments.length]);

  const getClassesForFund = (fundId) => fundClassesMap[fundId] || [];

  // Load fund classes on demand (for form dropdowns)
  const loadClassesForFund = async (fundId) => {
    if (!fundId || fundClassesMap[fundId]) return;
    try {
      const res = await fundAPI.getClasses(fundId);
      setFundClassesMap(prev => ({ ...prev, [fundId]: res.data || [] }));
    } catch { setFundClassesMap(prev => ({ ...prev, [fundId]: [] })); }
  };

  const getFaceValue = (fundId, className) => {
    const cls = getClassesForFund(fundId).find(c => c.name === className);
    return cls?.face_value || 0;
  };

  const handleAddTxnType = async () => {
    const name = newTxnType.trim();
    if (!name || txnTypes.includes(name)) return;
    const updated = [...txnTypes, name];
    try {
      await transactionTypesAPI.update({ types: updated });
      setTxnTypes(updated);
      setNewTxnType('');
      setShowNewTxnType(false);
    } catch (err) { alert('Failed to add type'); }
  };

  const handleEditTransaction = async () => {
    if (!editingTxn) return;
    try {
      await investorAccountAPI.updateTransaction(id, editingTxn.id, {
        date_of_transaction: editingTxn.date_of_transaction,
        transaction_type: editingTxn.transaction_type,
        transaction_amount: editingTxn.transaction_amount,
        class_of_units: editingTxn.class_of_units || null,
        isin: editingTxn.isin || null,
        is_pledged: editingTxn.is_pledged || false,
        remarks: editingTxn.remarks || null,
      });
      const [txnRes, invRes] = await Promise.all([
        investorAccountAPI.getTransactions(id),
        investorAccountAPI.getInvestments(id),
      ]);
      setTransactions(txnRes.data);
      setInvestments(invRes.data);
      setEditingTxn(null);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update'); }
  };

  // Investment Summary CRUD
  const handleCreateInvestment = async () => {
    if (!invForm.fund_id) return;
    try {
      await investorAccountAPI.createInvestment(id, {
        fund_id: invForm.fund_id,
        ppm_no: invForm.ppm_no,
        class_of_units: invForm.class_of_units || [],
        class_commitments: invForm.class_commitments || [],
        commitment_amount: invForm.commitment_amount || 0,
      });
      // Phase O — Refresh both investments AND transactions so any auto-created
      // "Commitment Agreed" transactions (one per class with an agreement_date)
      // are immediately visible in the Transactions tab.
      const [invRes, txnRes] = await Promise.all([
        investorAccountAPI.getInvestments(id),
        investorAccountAPI.getTransactions(id),
      ]);
      setInvestments(invRes.data);
      setTransactions(txnRes.data);
      setShowInvForm(false);
      setInvForm({ fund_id: '', ppm_no: '', class_of_units: [], class_commitments: [], commitment_amount: 0 });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateInvestment = async () => {
    if (!editingInv) return;
    try {
      // Detect re-up: any class whose new grouped total differs from the
      // grouped total at "edit-open" time (snapshot in `editingInv._initial_class_totals`).
      const before = editingInv._initial_class_totals || {};
      const afterRaw = editingInv.class_commitments || [];
      const after = afterRaw.reduce((acc, cc) => {
        const cn = (cc.class_name || '').trim();
        if (!cn) return acc;
        acc[cn] = (acc[cn] || 0) + Number(cc.amount || 0);
        return acc;
      }, {});
      const sameClassChanged = Object.keys(after).some(cn => (before[cn] || 0) !== after[cn] && (before[cn] || 0) > 0);

      await investorAccountAPI.updateInvestment(id, editingInv.id, {
        ppm_no: editingInv.ppm_no,
        class_of_units: editingInv.class_of_units,
        class_commitments: editingInv.class_commitments || [],
        commitment_amount: editingInv.commitment_amount,
      });

      // Re-up sync popup: only when an EXISTING class amount changed (new
      // classes are already auto-backfilled by the PUT endpoint). Asks the
      // user whether to retro-apply the new commitment to every past drawdown.
      if (sameClassChanged) {
        const changedClasses = Object.keys(after).filter(cn => (before[cn] || 0) !== after[cn] && (before[cn] || 0) > 0);
        const lines = changedClasses.map(cn => `  • ${cn}: ${Number(before[cn] || 0).toLocaleString()} → ${Number(after[cn]).toLocaleString()}`).join('\n');
        const proceed = window.confirm(
          `You've updated the commitment for the following class(es):\n\n${lines}\n\n` +
          `Should all past drawdowns be re-calculated using the new commitment for these classes?\n\n` +
          `(Receipts already received will be preserved — only the called/outstanding amounts will be updated.)`
        );
        if (proceed) {
          try {
            const res = await investorAccountAPI.syncPastDrawdowns(id, editingInv.id);
            const { updated_rows, drawdowns_touched } = res.data || {};
            if (updated_rows) {
              alert(`Past drawdowns updated: ${updated_rows} row(s) across ${drawdowns_touched} drawdown(s).`);
            } else {
              alert('No past drawdown rows needed an update.');
            }
          } catch (syncErr) {
            alert(syncErr.response?.data?.detail || 'Failed to sync past drawdowns. The commitment was saved successfully.');
          }
        }
      }

      const [invRes, txnRes] = await Promise.all([
        investorAccountAPI.getInvestments(id),
        investorAccountAPI.getTransactions(id),
      ]);
      setInvestments(invRes.data);
      setTransactions(txnRes.data);
      setEditingInv(null);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDeleteInvestment = async (invId) => {
    if (!window.confirm('Delete this investment record?')) return;
    await investorAccountAPI.deleteInvestment(id, invId);
    setInvestments(prev => prev.filter(i => i.id !== invId));
  };

  // Transactions
  const handleCreateTransaction = async () => {
    if (!txnForm.fund_id || !txnForm.date_of_transaction) return;
    try {
      await investorAccountAPI.createTransaction(id, txnForm);
      const [txnRes, invRes] = await Promise.all([
        investorAccountAPI.getTransactions(id),
        investorAccountAPI.getInvestments(id),
      ]);
      setTransactions(txnRes.data);
      setInvestments(invRes.data);
      setShowTxnForm(false);
      setTxnForm({ fund_id: '', date_of_transaction: '', transaction_type: 'Drawdown Received', transaction_amount: 0, class_of_units: '', isin: '', is_pledged: false, remarks: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteTransaction = async (txnId) => {
    if (!window.confirm('Delete this transaction?')) return;
    await investorAccountAPI.deleteTransaction(id, txnId);
    setSelectedTxns(prev => { const n = new Set(prev); n.delete(txnId); return n; });
    const [txnRes, invRes] = await Promise.all([
      investorAccountAPI.getTransactions(id),
      investorAccountAPI.getInvestments(id),
    ]);
    setTransactions(txnRes.data);
    setInvestments(invRes.data);
  };

  const handleDeleteSelectedTxns = async () => {
    if (selectedTxns.size === 0) return;
    if (!window.confirm(`Delete ${selectedTxns.size} selected transaction(s)?`)) return;
    try {
      await investorAccountAPI.bulkDeleteTransactions(id, [...selectedTxns]);
    } catch {}
    setSelectedTxns(new Set());
    const [txnRes, invRes] = await Promise.all([
      investorAccountAPI.getTransactions(id),
      investorAccountAPI.getInvestments(id),
    ]);
    setTransactions(txnRes.data);
    setInvestments(invRes.data);
  };

  const toggleTxnSelect = (txnId) => {
    setSelectedTxns(prev => {
      const n = new Set(prev);
      n.has(txnId) ? n.delete(txnId) : n.add(txnId);
      return n;
    });
  };

  const toggleAllTxnSelect = (txnList) => {
    const allSelected = txnList.every(t => selectedTxns.has(t.id));
    if (allSelected) {
      setSelectedTxns(new Set());
    } else {
      setSelectedTxns(new Set(txnList.map(t => t.id)));
    }
  };

  // Bulk upload handlers
  const handleBulkTxnUpload = async (data) => {
    try {
      const res = await investorAccountAPI.bulkUploadTransactions(id, data);
      if (res.data.success > 0) {
        const [txnRes, invRes] = await Promise.all([
          investorAccountAPI.getTransactions(id),
          investorAccountAPI.getInvestments(id),
        ]);
        setTransactions(txnRes.data);
        setInvestments(invRes.data);
      }
      return res.data;
    } catch (err) {
      return { success: 0, failed: data.length, errors: [err.response?.data?.detail || 'Upload failed'] };
    }
  };

  const handleBulkNavUpload = async (data) => {
    try {
      const res = await investorAccountAPI.bulkUploadNAVs(id, data);
      if (res.data.success > 0) {
        const [navRes, invRes] = await Promise.all([
          investorAccountAPI.getNAVs(id),
          investorAccountAPI.getInvestments(id),
        ]);
        setNavs(navRes.data);
        setInvestments(invRes.data);
      }
      return res.data;
    } catch (err) {
      return { success: 0, failed: data.length, errors: [err.response?.data?.detail || 'Upload failed'] };
    }
  };

  // NAVs
  const handleCreateNAV = async () => {
    if (!navForm.fund_id || !navForm.date_of_nav) return;
    try {
      await investorAccountAPI.createNAV(id, navForm);
      const [navRes, invRes] = await Promise.all([
        investorAccountAPI.getNAVs(id),
        investorAccountAPI.getInvestments(id),
      ]);
      setNavs(navRes.data);
      setInvestments(invRes.data);
      setShowNavForm(false);
      setNavForm({ fund_id: '', date_of_nav: '', nav_value: 0 });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteNAV = async (navId) => {
    if (!window.confirm('Delete this NAV record?')) return;
    await investorAccountAPI.deleteNAV(id, navId);
    const [navRes, invRes] = await Promise.all([
      investorAccountAPI.getNAVs(id),
      investorAccountAPI.getInvestments(id),
    ]);
    setNavs(navRes.data);
    setInvestments(invRes.data);
  };

  // Phase N — open class-wise NAV override editor
  const openOverrideNav = (n) => {
    const existing = Array.isArray(n.class_navs) ? n.class_navs : [];
    setEditingNav(n);
    if (existing.length) {
      setNavOverrideClasses(existing.map(c => ({
        class_name: c.class_name,
        units: Number(c.units || 0),
        nav_per_unit: Number(c.nav_per_unit || 0),
        overridden: Boolean(c.overridden),
      })));
    } else {
      // Legacy row with only nav_value — seed a single "Total" slot so the user
      // can at least enter one override. Fall back to fund-class names if known.
      setNavOverrideClasses([{
        class_name: 'Total',
        units: 0,
        nav_per_unit: Number(n.nav_value || 0),
        overridden: false,
      }]);
    }
  };

  const closeOverrideNav = () => {
    setEditingNav(null);
    setNavOverrideClasses([]);
  };

  const updateOverrideClass = (idx, field, value) => {
    setNavOverrideClasses(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const handleSaveOverrideNav = async () => {
    if (!editingNav) return;
    setNavOverrideSaving(true);
    try {
      await investorAccountAPI.updateNAV(id, editingNav.id, {
        class_navs: navOverrideClasses.map(c => ({
          class_name: c.class_name,
          units: Number(c.units || 0),
          nav_per_unit: Number(c.nav_per_unit || 0),
        })),
      });
      const [navRes, invRes] = await Promise.all([
        investorAccountAPI.getNAVs(id),
        investorAccountAPI.getInvestments(id),
      ]);
      setNavs(navRes.data);
      setInvestments(invRes.data);
      closeOverrideNav();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save override');
    } finally {
      setNavOverrideSaving(false);
    }
  };

  // Documentation handlers
  const handleCreateSection = async () => {
    if (!newSectionName.trim()) return;
    try {
      const res = await investorAccountAPI.createDocSection(id, { name: newSectionName.trim(), order: docSections.length });
      setDocSections(prev => [...prev, { ...res.data, items: [] }]);
      setExpandedSections(prev => ({ ...prev, [res.data.id]: true }));
      setNewSectionName('');
      setShowNewSection(false);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create section'); }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section and all its documents?')) return;
    try {
      await investorAccountAPI.deleteDocSection(id, sectionId);
      setDocSections(prev => prev.filter(s => s.id !== sectionId));
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateItem = async (sectionId) => {
    const name = (newItemName[sectionId] || '').trim();
    if (!name) return;
    try {
      const section = docSections.find(s => s.id === sectionId);
      const res = await investorAccountAPI.createDocItem(id, sectionId, { name, order: (section?.items || []).length });
      setDocSections(prev => prev.map(s => s.id === sectionId ? { ...s, items: [...(s.items || []), res.data] } : s));
      setNewItemName(prev => ({ ...prev, [sectionId]: '' }));
      setShowNewItem(prev => ({ ...prev, [sectionId]: false }));
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteItem = async (sectionId, itemId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await investorAccountAPI.deleteDocItem(id, itemId);
      setDocSections(prev => prev.map(s => s.id === sectionId ? { ...s, items: (s.items || []).filter(i => i.id !== itemId) } : s));
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleUpload = async (sectionId, itemId, file) => {
    setUploading(prev => ({ ...prev, [itemId]: true }));
    try {
      const res = await investorAccountAPI.uploadDocument(id, itemId, file);
      setDocSections(prev => prev.map(s => s.id === sectionId
        ? { ...s, items: (s.items || []).map(i => i.id === itemId ? res.data : i) }
        : s));
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(prev => ({ ...prev, [itemId]: false })); }
  };

  const handleDownload = async (itemId) => {
    try {
      const res = await investorAccountAPI.downloadDocument(id, itemId);
      window.open(res.data.download_url, '_blank');
    } catch (err) { alert(err.response?.data?.detail || 'Download failed'); }
  };

  // SOA Generation handlers
  const handleOpenSOAModal = async () => {
    setShowSOAModal(true);
    setSOAFundId('');
    setSOATemplates([]);
    setSOATemplateId('');
    setSOAFromDate('since_inception');
    setSOAToDate('');
    setSOAIncludeNav(true);
  };

  const handleSOAFundChange = async (fundId) => {
    setSOAFundId(fundId);
    setSOATemplateId('');
    if (fundId) {
      try {
        const res = await fundAPI.getSOATemplates(fundId);
        setSOATemplates(res.data);
      } catch (err) { setSOATemplates([]); }
    } else {
      setSOATemplates([]);
    }
  };

  const handleGenerateSOA = async () => {
    if (!soaFundId || !soaTemplateId) return;
    setSOAGenerating(true);
    try {
      const fd = soaFromDate === 'since_inception' ? 'since_inception' : soaFromDate;
      const res = await investorAccountAPI.generateSOA(id, soaFundId, soaTemplateId, fd || null, soaToDate || null, soaIncludeNav);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      // Use filename from Content-Disposition header if available
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `SOA_${account.account_name}.pdf`.replace(/\s/g, '_');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowSOAModal(false);
    } catch (err) { alert(err.response?.data?.detail || 'SOA generation failed'); }
    finally { setSOAGenerating(false); }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!account) return <div className="text-center py-12">Account not found</div>;

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const isIndividual = account.type_of_investor === 'Individual';
  const tabs = [
    { id: 'info', label: 'Account Info' },
    { id: 'contacts', label: 'Contact & Address' },
    { id: 'fatca', label: 'FATCA / CRS' },
    { id: 'investments', label: 'Investment Summary' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'navs', label: 'NAV' },
    { id: 'bank_demat', label: 'Bank & Demat' },
    // Nomination is an Individual-only concept (only natural persons can
    // appoint nominees). Non-Individual accounts see the tab greyed-out
    // and can't click into it.
    { id: 'nomination', label: 'Nomination', disabled: !isIndividual,
      disabledReason: 'Nomination is only available for Individual investors.' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'docs', label: 'Documentation' },
    ...(isAdmin ? [{ id: 'history', label: 'History' }] : []),
  ];

  const filteredTxns = (() => {
    // "Drawdown Called" is not a ledger transaction (it's a drawdown stage-state),
    // never show it in the Transactions tab even if a legacy row exists.
    let list = (fundFilter ? transactions.filter(t => t.fund_id === fundFilter) : [...transactions])
      .filter(t => t.transaction_type !== 'Drawdown Called');
    list.sort((a, b) => {
      const dA = a.date_of_transaction || '';
      const dB = b.date_of_transaction || '';
      return txnSortDir === 'asc' ? dA.localeCompare(dB) : dB.localeCompare(dA);
    });
    return list;
  })();
  const filteredNavs = fundFilter ? navs.filter(n => n.fund_id === fundFilter) : navs;
  const fmt = (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

  const convertToDisplayCurrency = (amount, sourceCurrency) => {
    if (amount == null) return 0;
    const src = (sourceCurrency || 'INR').toUpperCase();
    const tgt = selectedCurrency.toUpperCase();
    if (src === tgt) return amount;
    if (src === 'INR' && tgt === 'USD') return amount / exchangeRate;
    if (src === 'USD' && tgt === 'INR') return amount * exchangeRate;
    return amount;
  };

  const formatDisplayAmount = (displayAmount) => {
    if (displayAmount == null) return '-';
    const sym = selectedCurrency === 'INR' ? '\u20B9' : '$';
    const locale = selectedCurrency === 'INR' ? 'en-IN' : 'en-US';
    switch (currencyFormat) {
      case 'crores': return `${sym}${(displayAmount / 10000000).toFixed(2)} Cr`;
      case 'millions': return `${sym}${(displayAmount / 1000000).toFixed(2)} M`;
      default: return new Intl.NumberFormat(locale, { style: 'currency', currency: selectedCurrency, maximumFractionDigits: 0 }).format(displayAmount);
    }
  };

  const formatAmount = (amount, fundId) => {
    if (amount == null) return '-';
    const fundCcy = fundId ? (getFund(fundId)?.fund_currency || 'INR') : 'INR';
    return formatDisplayAmount(convertToDisplayCurrency(Number(amount), fundCcy));
  };

  const invTotals = investments.reduce((acc, inv) => {
    const fundCcy = getFund(inv.fund_id)?.fund_currency || 'INR';
    return {
      commitment: acc.commitment + convertToDisplayCurrency(inv.commitment_amount || 0, fundCcy),
      called: acc.called + convertToDisplayCurrency(inv.called_amount || 0, fundCcy),
      pending: acc.pending + convertToDisplayCurrency(inv.pending_receipt_amount || 0, fundCcy),
      contribution: acc.contribution + convertToDisplayCurrency(inv.contribution_amount || 0, fundCcy),
      distribution: acc.distribution + convertToDisplayCurrency(inv.distribution_amount || 0, fundCcy),
      nav: acc.nav + convertToDisplayCurrency(inv.nav_value || 0, fundCcy),
    };
  }, { commitment: 0, called: 0, pending: 0, contribution: 0, distribution: 0, nav: 0 });

  const fundCurrencies = [...new Set(investments.map(inv => getFund(inv.fund_id)?.fund_currency).filter(Boolean))];
  const hasMultipleCurrencies = fundCurrencies.length > 1;
  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // Bulk upload template data
  const txnFundNames = investmentFunds.map(f => f.name).join(', ');
  const txnTemplateData = { Fund: '', Date: 'YYYY-MM-DD', Type: 'Drawdown Received', Amount: 0, 'Class of Units': '', 'Drawdown Number': '', Remarks: '' };
  const navTemplateData = { Fund: '', Date: 'YYYY-MM-DD', 'NAV Value': 0 };

  return (
    <div data-testid="investor-account-details-page">
      <button onClick={() => navigate('/investors/accounts')} className="flex items-center text-gray-600 hover:text-gray-900 mb-6" data-testid="back-btn">
        <ArrowLeft size={20} className="mr-2" /> Back to Accounts
      </button>

      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="account-name">{account.account_name}</h1>
            <div className="flex gap-3 mt-2 text-sm text-gray-500">
              <span className="px-2 py-0.5 bg-gray-100 rounded">{account.type_of_investor}</span>
              {account.pan && <span>PAN: {account.pan}</span>}
              {account.contact_name && <span>Contact: {account.contact_name}</span>}
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button onClick={handleOpenSOAModal} className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-testid="generate-soa-btn">
                <FileText size={16} className="mr-2" /> Generate SOA
              </button>
              <button onClick={() => navigate(`/investors/accounts/${id}/edit`)} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="edit-btn">
                <Edit size={16} className="mr-2" /> Edit
              </button>
            </div>
          )}
        </div>

        <div className="border-b"><div className="flex overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id}
              onClick={() => { if (!tab.disabled) setActiveTab(tab.id); }}
              disabled={tab.disabled}
              title={tab.disabled ? tab.disabledReason : undefined}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab.disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}>{tab.label}</button>
          ))}
        </div></div>

        <div className="p-6">
          {/* Account Info Tab */}
          {activeTab === 'info' && (
            <div data-testid="tab-content-info">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Info label="Account Name" value={account.account_name} />
                <Info label="DOB / Incorporation" value={account.date_of_birth_or_incorporation} />
                <Info label="Linked Contact" value={account.contact_name} />
                <Info label="Type of Investor" value={account.type_of_investor === 'Other' ? account.type_of_investor_other : account.type_of_investor} />
                <Info label="Mode of Holding" value={account.mode_of_holding || 'Single'} />
                {!isIndividual && <Info label="Law Under Formed" value={account.law_under_formed} />}
                <Info label="PAN" value={account.pan} />
                <Info label="Part of First Close" value={account.part_of_first_close || '--'} />
                <Info label="Investor Lead" value={account.investor_lead_name} />
                <Info label="Distributor" value={account.distributor_name} />
              </div>
              <SectionChangeHistory entries={account.account_info_history} testIdPrefix="account-info-history" />
            </div>
          )}

          {/* Contact & Address Tab */}
          {activeTab === 'contacts' && (
            <div data-testid="tab-content-contacts" className="space-y-6">
              <PersonList title="Primary Contacts" list={account.primary_contacts} />
              <PersonList title="Additional Contacts" list={account.additional_contacts} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AddrView title="Correspondence Address" addr={account.correspondence_address} />
                <AddrView title="Registered / Permanent Address" addr={account.registered_address} />
              </div>
              <SectionChangeHistory entries={account.address_history} testIdPrefix="address-history" />
            </div>
          )}

          {/* FATCA Tab */}
          {activeTab === 'fatca' && (
            <div data-testid="tab-content-fatca">
              {isIndividual && account.fatca_individual ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Info label="Place of Birth" value={account.fatca_individual.place_of_birth} />
                  <Info label="Country of Birth" value={account.fatca_individual.country_of_birth} />
                  <Info label="Country of Citizenship" value={account.fatca_individual.country_of_citizenship} />
                  <Info label="US Citizen/Resident" value={account.fatca_individual.us_citizen_or_resident} />
                  {account.fatca_individual.us_citizen_or_resident === 'Yes' && <Info label="US TIN" value={account.fatca_individual.us_tin} />}
                  <Info label="Tax Residency Country" value={account.fatca_individual.country_of_tax_residency} />
                  <Info label="TIN Type" value={account.fatca_individual.tin_type} />
                  <Info label="TIN" value={account.fatca_individual.tin} />
                </div>
              ) : account.fatca_non_individual ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Info label="Is US Person" value={account.fatca_non_individual.is_us_person} />
                  {account.fatca_non_individual.is_us_person === 'Yes' && <Info label="US TIN" value={account.fatca_non_individual.us_tin} />}
                  <Info label="Formed Outside India" value={account.fatca_non_individual.formed_outside_india} />
                  <Info label="Tax Residency Other Than India" value={account.fatca_non_individual.tax_residency_other_than_india} />
                  <Info label="Financial Institution" value={account.fatca_non_individual.is_financial_institution} />
                  {account.fatca_non_individual.is_financial_institution === 'Yes' && <Info label="GIIN" value={account.fatca_non_individual.giin} />}
                  <Info label="Publicly Traded" value={account.fatca_non_individual.is_publicly_traded} />
                  <Info label="Controlling Persons Not Indian" value={account.fatca_non_individual.controlling_persons_not_indian} />
                </div>
              ) : <p className="text-gray-500">No FATCA/CRS data</p>}
              <SectionChangeHistory entries={account.fatca_history} testIdPrefix="fatca-history" />
            </div>
          )}

          {/* Investments Tab */}
          {activeTab === 'investments' && (
            <div data-testid="tab-content-investments">
              <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
                <h3 className="text-lg font-semibold">Investment Summary (Fund-wise)</h3>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Currency toggle is always available so users can convert
                      a single-currency view between INR and USD on demand. */}
                  <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-1.5">
                    <select value={selectedCurrency} onChange={e => setSelectedCurrency(e.target.value)} className="border-0 text-sm focus:outline-none focus:ring-0 bg-transparent" data-testid="inv-currency-select">
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                    </select>
                    {selectedCurrency === 'USD' && (
                      <>
                        <input type="number" value={exchangeRate} onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)} step="0.01" min="0" className="border-0 text-sm focus:outline-none focus:ring-0 w-16 bg-transparent" />
                        <span className="text-xs text-gray-500">INR/USD</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="inv-format-selector">
                    {['full', 'crores', 'millions'].map(f => (
                      <button key={f} onClick={() => setCurrencyFormat(f)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${currencyFormat === f ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        data-testid={`format-${f}`}>
                        {f === 'full' ? 'Full' : f === 'crores' ? 'Crores' : 'Millions'}
                      </button>
                    ))}
                  </div>
                  {canEdit && <button onClick={() => { setShowInvForm(true); setEditingInv(null); }} className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-investment-btn"><Plus size={16} className="mr-1" /> Add Fund</button>}
                </div>
              </div>

              {/* Summary Cards */}
              {investments.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="inv-summary-cards">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">Total Commitment</p>
                    <p className="text-lg font-bold text-blue-900" data-testid="total-commitment">{formatDisplayAmount(invTotals.commitment)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
                    <p className="text-xs text-indigo-600 font-medium mb-1">Total Called</p>
                    <p className="text-lg font-bold text-indigo-900" data-testid="total-called">{formatDisplayAmount(invTotals.called)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <p className="text-xs text-green-600 font-medium mb-1">Total Contribution</p>
                    <p className="text-lg font-bold text-green-900" data-testid="total-contribution">{formatDisplayAmount(invTotals.contribution)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                    <p className="text-xs text-red-600 font-medium mb-1">Pending Receipt</p>
                    <p className="text-lg font-bold text-red-900" data-testid="total-pending-receipt">{formatDisplayAmount(invTotals.pending)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium mb-1">Total Distribution</p>
                    <p className="text-lg font-bold text-amber-900" data-testid="total-distribution">{formatDisplayAmount(invTotals.distribution)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium mb-1">Total NAV</p>
                    <p className="text-lg font-bold text-purple-900" data-testid="total-nav">{formatDisplayAmount(invTotals.nav)}</p>
                  </div>
                </div>
              )}

              {/* Pledged Units Summary */}
              {pledgedSummary && pledgedSummary.has_pledged && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="pledged-summary-card">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] rounded font-semibold">PLEDGED</span>
                    <span className="text-xs text-red-700 font-medium">Units Status</span>
                  </div>
                  <div>
                    <p className="text-xs text-red-600">Pledged Units</p>
                    <p className="text-sm font-bold text-red-900" data-testid="pledged-units">{fmt(pledgedSummary.pledged_units)} <span className="text-[10px] text-red-500">({pledgedSummary.pledged_count} txn)</span></p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600">Free Units</p>
                    <p className="text-sm font-bold text-green-900" data-testid="free-units">{fmt(pledgedSummary.free_units)} <span className="text-[10px] text-green-500">({pledgedSummary.free_count} txn)</span></p>
                  </div>
                </div>
              )}

              {/* Add / Edit Investment Form */}
              {(showInvForm || editingInv) && (() => {
                const isEditing = Boolean(editingInv);
                const formData = isEditing ? editingInv : invForm;
                const setFormData = isEditing
                  ? (updater) => setEditingInv(prev => typeof updater === 'function' ? updater(prev) : updater)
                  : (updater) => setInvForm(prev => typeof updater === 'function' ? updater(prev) : updater);

                const selectedFundId = formData.fund_id;
                const fundClasses = getClassesForFund(selectedFundId);
                const classCommitments = formData.class_commitments || [];
                // Unique class names derived from commitment rows
                const uniqueClasses = Array.from(new Set(classCommitments.map(cc => cc.class_name).filter(Boolean)));
                const totalCommitment = classCommitments.reduce((sum, cc) => sum + (Number(cc.amount) || 0), 0);

                const syncCommitments = (next) => {
                  const uniq = Array.from(new Set(next.map(cc => cc.class_name).filter(Boolean)));
                  const total = next.reduce((s, cc) => s + (Number(cc.amount) || 0), 0);
                  return { class_commitments: next, class_of_units: uniq, commitment_amount: total };
                };

                const handleFundChange = (fId) => {
                  setFormData(p => ({ ...p, fund_id: fId, class_of_units: [], class_commitments: [], commitment_amount: 0 }));
                  loadClassesForFund(fId);
                };

                // Phase O2 — Row-based commitment editor: multiple rows per class
                // (re-ups) are allowed. Each row carries its own agreement_date.
                const handleAddCommitmentRow = (className = '') => {
                  setFormData(prev => {
                    const next = [...(prev.class_commitments || []), { class_name: className, amount: 0, agreement_date: '' }];
                    return { ...prev, ...syncCommitments(next) };
                  });
                };

                const handleRemoveCommitmentRow = (idx) => {
                  setFormData(prev => {
                    const next = (prev.class_commitments || []).filter((_, i) => i !== idx);
                    return { ...prev, ...syncCommitments(next) };
                  });
                };

                const handleCommitmentRowChange = (idx, field, value) => {
                  setFormData(prev => {
                    const next = (prev.class_commitments || []).map((cc, i) => {
                      if (i !== idx) return cc;
                      if (field === 'amount') return { ...cc, amount: parseFloat(value) || 0 };
                      return { ...cc, [field]: value };
                    });
                    return { ...prev, ...syncCommitments(next) };
                  });
                };

                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="investment-form">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">{isEditing ? 'Edit Investment' : 'Add Fund Investment'}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Fund {isEditing ? '' : '*'}</label>
                        {isEditing ? (
                          <input type="text" readOnly value={formData.fund_name || ''} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500" />
                        ) : (
                          <select value={formData.fund_id} onChange={e => handleFundChange(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="inv-fund-select">
                            <option value="">Select Fund</option>
                            {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                          </select>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700">PPM No.</label>
                          {formData.fund_id && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API_URL}/funds/${formData.fund_id}/next-ppm`);
                                  setFormData(p => ({ ...p, ppm_no: res.data.ppm_no }));
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Could not fetch next PPM');
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                              data-testid="inv-suggest-ppm"
                              title="Fill with the next free PPM in this fund"
                            >
                              Suggest next
                            </button>
                          )}
                        </div>
                        <input type="text" value={formData.ppm_no || ''} onChange={e => setFormData(p => ({ ...p, ppm_no: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="inv-ppm-input" />
                        {(() => {
                          const selectedFund = funds.find(f => f.id === (isEditing ? formData.fund_id : formData.fund_id));
                          const suffix = selectedFund?.fund_suffix;
                          const ppm = formData.ppm_no;
                          if (suffix && ppm) {
                            return <p className="text-xs text-blue-600 mt-1">Folio No: <span className="font-semibold">{suffix}-{ppm}</span></p>;
                          }
                          return null;
                        })()}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Total Commitment</label>
                        <input type="text" readOnly value={classCommitments.length > 0 ? totalCommitment.toLocaleString() : (formData.commitment_amount || 0)}
                          className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500 font-semibold"
                          data-testid="inv-commitment-total" />
                        {classCommitments.length === 0 && (
                          <input type="number" value={formData.commitment_amount || 0} onChange={e => setFormData(p => ({ ...p, commitment_amount: parseFloat(e.target.value) || 0 }))}
                            className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="inv-commitment-input"
                            placeholder="Enter commitment (no classes added)" />
                        )}
                      </div>
                    </div>

                    {/* Phase O2 — Row-based class commitments (multiple per class allowed) */}
                    {selectedFundId && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Class Commitments</label>
                          <button type="button" onClick={() => handleAddCommitmentRow('')}
                            disabled={fundClasses.length === 0}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40"
                            data-testid="inv-add-commitment-row">
                            + Add Commitment
                          </button>
                        </div>
                        {fundClasses.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">No unit classes defined for this fund. Add them in Fund settings.</p>
                        ) : classCommitments.length === 0 ? (
                          <p className="text-xs text-gray-500 italic">No commitments yet. Click "+ Add Commitment" to begin. Investors with re-ups can have multiple entries for the same class with different Agreement Dates.</p>
                        ) : (
                          <div className="space-y-2" data-testid="inv-commitment-rows">
                            {classCommitments.map((cc, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 bg-white flex-wrap" data-testid={`inv-commitment-row-${idx}`}>
                                <select
                                  value={cc.class_name || ''}
                                  onChange={e => handleCommitmentRowChange(idx, 'class_name', e.target.value)}
                                  className="border rounded-lg px-2 py-1.5 text-sm min-w-[140px]"
                                  data-testid={`inv-commitment-class-${idx}`}
                                >
                                  <option value="">Select Class</option>
                                  {fundClasses.map(fc => (
                                    <option key={fc.id || fc.name} value={fc.name}>{fc.name}</option>
                                  ))}
                                </select>
                                <span className="text-xs text-gray-500 whitespace-nowrap">Commitment:</span>
                                <input type="number" value={cc.amount || ''}
                                  onChange={e => handleCommitmentRowChange(idx, 'amount', e.target.value)}
                                  placeholder="0"
                                  className="border rounded-lg px-3 py-1.5 text-sm flex-1 min-w-0"
                                  data-testid={`inv-commitment-amount-${idx}`} />
                                <span className="text-xs text-gray-500 whitespace-nowrap">Agreement Date:</span>
                                <input type="date" value={cc.agreement_date || ''}
                                  onChange={e => handleCommitmentRowChange(idx, 'agreement_date', e.target.value)}
                                  className="border rounded-lg px-2 py-1.5 text-sm"
                                  data-testid={`inv-commitment-date-${idx}`} />
                                <button type="button" onClick={() => handleRemoveCommitmentRow(idx)}
                                  className="text-red-500 hover:text-red-700 px-2"
                                  data-testid={`inv-commitment-remove-${idx}`} aria-label="Remove">
                                  ×
                                </button>
                              </div>
                            ))}
                            {classCommitments.length > 0 && (
                              <div className="flex justify-between pt-1 text-sm">
                                <span className="text-gray-500">
                                  Classes: {uniqueClasses.join(', ') || '—'}
                                </span>
                                <span className="font-semibold text-blue-700" data-testid="inv-class-total">
                                  Total: {totalCommitment.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={() => { setShowInvForm(false); setEditingInv(null); }} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
                      <button onClick={isEditing ? handleUpdateInvestment : handleCreateInvestment} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg" data-testid="save-investment-btn">
                        <Save size={14} className="mr-1 inline" /> {isEditing ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {investments.length === 0 ? <p className="text-gray-500 text-center py-6">No investments yet</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="investments-table">
                    <thead><tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                      <th className="px-3 py-2">Fund</th><th className="px-3 py-2">Currency</th><th className="px-3 py-2">Folio No.</th><th className="px-3 py-2">PPM No.</th>
                      <th className="px-3 py-2">Class</th>
                      <th className="px-3 py-2">ISIN</th>
                      <th className="px-3 py-2 text-right">Commitment</th>
                      <th className="px-3 py-2 text-right">Called</th>
                      <th className="px-3 py-2 text-right">Contribution</th>
                      <th className="px-3 py-2 text-right">Pending Receipt</th>
                      <th className="px-3 py-2 text-right">Units Issued</th>
                      <th className="px-3 py-2 text-right">Distribution</th><th className="px-3 py-2 text-right">NAV</th>
                      <th className="px-3 py-2 text-right">Investor IRR (%)</th><th className="px-3 py-2 w-20"></th>
                    </tr></thead>
                    <tbody>
                      {investments.map(inv => {
                        const fund = getFund(inv.fund_id);
                        // Prefer the server-computed class_breakdown; fall back to a
                        // single-row mock derived from class_commitments for legacy
                        // payloads that haven't been refreshed yet.
                        let breakdown = inv.class_breakdown;
                        if (!Array.isArray(breakdown) || breakdown.length === 0) {
                          const ccGrouped = (inv.class_commitments || []).reduce((acc, cc) => {
                            const cn = (cc.class_name || '').trim();
                            if (!cn) return acc;
                            acc[cn] = (acc[cn] || 0) + Number(cc.amount || 0);
                            return acc;
                          }, {});
                          breakdown = Object.entries(ccGrouped).map(([cn, amt]) => ({
                            class_name: cn, isin: '', commitment: amt,
                            called: 0, contribution: 0, pending_receipt: 0,
                            units_issued: 0, distribution: 0, nav: 0,
                          }));
                        }
                        // If we STILL have nothing (no classes at all), synthesise a
                        // single bucket so the fund still gets a visible row.
                        const rowsToRender = breakdown.length > 0 ? breakdown : [{
                          class_name: '—',
                          isin: '',
                          commitment: inv.commitment_amount || 0,
                          called: inv.called_amount || 0,
                          contribution: inv.contribution_amount || 0,
                          pending_receipt: inv.pending_receipt_amount || 0,
                          units_issued: 0,
                          distribution: inv.distribution_amount || 0,
                          nav: inv.nav_value || 0,
                        }];
                        return (
                          <React.Fragment key={inv.id}>
                            {rowsToRender.map((cb, idx) => {
                              const trancheKey = `${inv.id}::${cb.class_name}`;
                              const tranches = Array.isArray(cb.tranches) ? cb.tranches : [];
                              const canExpand = tranches.length > 0;
                              const isExpanded = !!expandedTranches[trancheKey];
                              return (
                              <React.Fragment key={`${inv.id}-${cb.class_name}-${idx}`}>
                              <tr className="border-b hover:bg-gray-50" data-testid={`inv-class-row-${inv.id}-${cb.class_name}`}>
                                {idx === 0 ? (
                                  <>
                                    <td className="px-3 py-2 font-medium align-top" rowSpan={rowsToRender.length}>{inv.fund_name}</td>
                                    <td className="px-3 py-2 text-gray-500 align-top" rowSpan={rowsToRender.length}>{fund?.fund_currency || inv.currency || '-'}</td>
                                    <td className="px-3 py-2 text-gray-600 align-top" rowSpan={rowsToRender.length}>{inv.folio_no || '-'}</td>
                                    <td className="px-3 py-2 text-gray-600 align-top" rowSpan={rowsToRender.length}>{inv.ppm_no || '-'}</td>
                                  </>
                                ) : null}
                                <td className="px-3 py-2 text-gray-700 font-medium">
                                  <div className="inline-flex items-center gap-1.5">
                                    {canExpand ? (
                                      <button
                                        type="button"
                                        onClick={() => setExpandedTranches((prev) => ({ ...prev, [trancheKey]: !prev[trancheKey] }))}
                                        className="p-0.5 rounded hover:bg-blue-100 text-blue-600"
                                        title={isExpanded ? 'Hide tranches' : `Show ${tranches.length} tranche${tranches.length > 1 ? 's' : ''}`}
                                        data-testid={`toggle-tranches-${inv.id}-${cb.class_name}`}
                                      >
                                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                      </button>
                                    ) : (
                                      <span className="inline-block w-[18px]" />
                                    )}
                                    <span>{cb.class_name}</span>
                                    {canExpand && (
                                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-50 text-blue-700 border border-blue-200">
                                        {tranches.length}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-gray-600 font-mono text-xs">{cb.isin || '-'}</td>
                                <td className="px-3 py-2 text-right">{formatAmount(cb.commitment, inv.fund_id)}</td>
                                <td className="px-3 py-2 text-right text-blue-700">{formatAmount(cb.called, inv.fund_id)}</td>
                                <td className="px-3 py-2 text-right">{formatAmount(cb.contribution, inv.fund_id)}</td>
                                <td className={`px-3 py-2 text-right font-medium ${(cb.pending_receipt || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>{formatAmount(cb.pending_receipt, inv.fund_id)}</td>
                                <td className="px-3 py-2 text-right">{(cb.units_issued != null && cb.units_issued !== 0) ? Number(cb.units_issued).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'}</td>
                                <td className="px-3 py-2 text-right">{formatAmount(cb.distribution, inv.fund_id)}</td>
                                <td className="px-3 py-2 text-right">{formatAmount(cb.nav, inv.fund_id)}</td>
                                {idx === 0 ? (
                                  <>
                                    <td className="px-3 py-2 text-right font-medium align-top" rowSpan={rowsToRender.length}>{inv.investor_irr != null ? `${inv.investor_irr}%` : '-'}</td>
                                    <td className="px-3 py-2 align-top" rowSpan={rowsToRender.length}>
                                      <div className="flex items-center gap-1">
                                        {canEdit && <button onClick={() => {
                                          loadClassesForFund(inv.fund_id);
                                          const initialTotals = (inv.class_commitments || []).reduce((acc, cc) => {
                                            const cn = (cc.class_name || '').trim();
                                            if (!cn) return acc;
                                            acc[cn] = (acc[cn] || 0) + Number(cc.amount || 0);
                                            return acc;
                                          }, {});
                                          setEditingInv({ ...inv, class_commitments: inv.class_commitments || [], _initial_class_totals: initialTotals });
                                          setShowInvForm(false);
                                        }} className="p-1 text-gray-400 hover:text-blue-600" data-testid={`edit-inv-${inv.id}`}><Edit size={14} /></button>}
                                        {canDelete && <button onClick={() => handleDeleteInvestment(inv.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>}
                                      </div>
                                    </td>
                                  </>
                                ) : null}
                              </tr>
                              {isExpanded && canExpand && (
                                <tr className="bg-blue-50/40 border-b" data-testid={`tranche-detail-${inv.id}-${cb.class_name}`}>
                                  <td colSpan={6} />
                                  <td colSpan={7} className="px-3 py-2">
                                    <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                                      Tranche ledger · {tranches.length} entr{tranches.length === 1 ? 'y' : 'ies'}
                                    </div>
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-gray-500 border-b border-blue-100">
                                          <th className="text-left px-2 py-1 font-medium">Agreement Date</th>
                                          <th className="text-right px-2 py-1 font-medium">Amount</th>
                                          <th className="text-left px-2 py-1 font-medium">Source</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {tranches.map((t, ti) => (
                                          <tr key={t.id || ti} data-testid={`tranche-row-${inv.id}-${cb.class_name}-${ti}`}>
                                            <td className="px-2 py-1 text-gray-700">{t.agreement_date || '-'}</td>
                                            <td className="px-2 py-1 text-right text-gray-800 font-medium">{formatAmount(t.amount, inv.fund_id)}</td>
                                            <td className="px-2 py-1 text-gray-600">
                                              {t.transfer_reference
                                                ? <span className="inline-flex items-center gap-1 text-blue-700"><ArrowLeftRight size={11} /> {t.transfer_reference}</span>
                                                : <span className="text-gray-400 italic">Original subscription</span>}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                  {/* Trailing 2 tds fill the IRR + Actions columns so the sub-row spans the full 15-column parent grid. */}
                                  <td colSpan={2} />
                                </tr>
                              )}
                              </React.Fragment>
                            );})}
                            {/* Per-fund subtotal row */}
                            <tr className="bg-slate-50 border-b text-slate-700 text-xs font-semibold" data-testid={`inv-fund-total-${inv.id}`}>
                              <td className="px-3 py-1.5" colSpan={6}>
                                <div className="inline-flex items-center gap-2">
                                  <span>Total — {inv.fund_name}</span>
                                  <button
                                    type="button"
                                    onClick={() => openHistory(inv.fund_id, inv.fund_name)}
                                    className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 border border-blue-200 hover:border-blue-400 bg-blue-50 hover:bg-blue-100 rounded px-1.5 py-0.5 font-medium transition-colors"
                                    title="View transfer history for this fund"
                                    data-testid={`transfer-history-btn-${inv.id}`}
                                  >
                                    <History size={11} /> Transfer History
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-1.5 text-right">{formatAmount(inv.commitment_amount, inv.fund_id)}</td>
                              <td className="px-3 py-1.5 text-right text-blue-700">{formatAmount(inv.called_amount || 0, inv.fund_id)}</td>
                              <td className="px-3 py-1.5 text-right">{formatAmount(inv.contribution_amount, inv.fund_id)}</td>
                              <td className={`px-3 py-1.5 text-right ${(inv.pending_receipt_amount || 0) > 0 ? 'text-red-600' : 'text-gray-500'}`}>{formatAmount(inv.pending_receipt_amount || 0, inv.fund_id)}</td>
                              <td className="px-3 py-1.5 text-right">{(() => {
                                const uTotal = (inv.class_breakdown || []).reduce((s, cb) => s + Number(cb.units_issued || 0), 0);
                                return uTotal ? uTotal.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-';
                              })()}</td>
                              <td className="px-3 py-1.5 text-right">{formatAmount(inv.distribution_amount, inv.fund_id)}</td>
                              <td className="px-3 py-1.5 text-right">{formatAmount(inv.nav_value, inv.fund_id)}</td>
                              <td className="px-3 py-1.5" colSpan={2}></td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                      {/* Grand total across funds — currency-aware via formatDisplayAmount. */}
                      <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300" data-testid="inv-grand-total-row">
                        <td className="px-3 py-2" colSpan={6}>{investments.length > 1 ? 'Grand Total' : 'Total'}</td>
                        <td className="px-3 py-2 text-right">{formatDisplayAmount(invTotals.commitment)}</td>
                        <td className="px-3 py-2 text-right text-blue-700">{formatDisplayAmount(invTotals.called)}</td>
                        <td className="px-3 py-2 text-right">{formatDisplayAmount(invTotals.contribution)}</td>
                        <td className="px-3 py-2 text-right text-red-600">{formatDisplayAmount(invTotals.pending)}</td>
                        <td className="px-3 py-2 text-right">{(() => {
                          const uTotal = investments.reduce((s, i) => s + (i.class_breakdown || []).reduce((s2, cb) => s2 + Number(cb.units_issued || 0), 0), 0);
                          return uTotal ? uTotal.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-';
                        })()}</td>
                        <td className="px-3 py-2 text-right">{formatDisplayAmount(invTotals.distribution)}</td>
                        <td className="px-3 py-2 text-right">{formatDisplayAmount(invTotals.nav)}</td>
                        <td className="px-3 py-2" colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div data-testid="tab-content-transactions">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">Transaction Details</h3>
                  <select value={fundFilter} onChange={e => setFundFilter(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" data-testid="txn-fund-filter">
                    <option value="">All Funds</option>
                    {investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {canDelete && selectedTxns.size > 0 && (
                    <button onClick={handleDeleteSelectedTxns} className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="delete-selected-txn-btn">
                      <Trash2 size={16} className="mr-1" /> Delete ({selectedTxns.size})
                    </button>
                  )}
                  {canEdit && (
                    <>
                      <button onClick={() => setShowBulkTxn(true)} className="inline-flex items-center px-3 py-2 border border-blue-600 text-blue-600 rounded-lg text-sm hover:bg-blue-50" data-testid="bulk-upload-txn-btn">
                        <Upload size={16} className="mr-1" /> Bulk Upload
                      </button>
                      <button onClick={() => { setShowTxnForm(true); setEditingTxn(null); }} className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-txn-btn">
                        <Plus size={16} className="mr-1" /> Add Transaction
                      </button>
                    </>
                  )}
                </div>
              </div>

              {showNewTxnType && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3 flex items-end gap-3" data-testid="new-txn-type-form">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-600">New Transaction Type</label>
                    <input type="text" value={newTxnType} onChange={e => setNewTxnType(e.target.value)} placeholder="e.g. Capital Call"
                      className="w-full border rounded px-3 py-1.5 text-sm mt-1" onKeyDown={e => e.key === 'Enter' && handleAddTxnType()} autoFocus data-testid="new-type-input" />
                  </div>
                  <button onClick={() => { setShowNewTxnType(false); setNewTxnType(''); }} className="px-2 py-1.5 text-xs border rounded text-gray-600">Cancel</button>
                  <button onClick={handleAddTxnType} className="px-2 py-1.5 text-xs bg-yellow-600 text-white rounded" data-testid="save-type-btn">Add</button>
                </div>
              )}

              {(showTxnForm || editingTxn) && (() => {
                const formData = editingTxn || txnForm;
                const setFormData = editingTxn
                  ? (updater) => setEditingTxn(prev => typeof updater === 'function' ? updater(prev) : updater)
                  : (updater) => setTxnForm(prev => typeof updater === 'function' ? updater(prev) : updater);
                const isEditing = Boolean(editingTxn);
                const selectedFundId = formData.fund_id;
                const fundCurrency = selectedFundId ? (getFund(selectedFundId)?.fund_currency || '-') : '';
                
                // Filter classes: only show classes selected in investment summary for this fund
                const fundInvestment = investments.find(inv => inv.fund_id === selectedFundId);
                const investmentClasses = fundInvestment?.class_of_units || [];
                const allFundClasses = getClassesForFund(selectedFundId);
                const filteredClasses = investmentClasses.length > 0
                  ? allFundClasses.filter(c => investmentClasses.includes(c.name))
                  : allFundClasses;
                
                const faceVal = getFaceValue(selectedFundId, formData.class_of_units);
                const computedUnits = faceVal > 0 && formData.transaction_amount ? (formData.transaction_amount / faceVal) : null;

                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="txn-form">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">{isEditing ? 'Edit Transaction' : 'New Transaction'}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div><label className="text-sm font-medium">Fund *</label>
                        {isEditing ? (
                          <input type="text" readOnly value={formData.fund_name || getFund(formData.fund_id)?.name || ''} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500" />
                        ) : (
                          <select value={formData.fund_id} onChange={e => setFormData(p => ({ ...p, fund_id: e.target.value, class_of_units: '' }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="txn-fund-select">
                            <option value="">Select</option>{investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                          </select>
                        )}
                      </div>
                      <div><label className="text-sm font-medium">Date *</label>
                        <input type="date" value={formData.date_of_transaction} onChange={e => setFormData(p => ({ ...p, date_of_transaction: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="txn-date-input" /></div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Type</label>
                          {canEdit && !showNewTxnType && <button type="button" onClick={() => setShowNewTxnType(true)} className="text-xs text-blue-600 hover:text-blue-800">+ Add Type</button>}
                        </div>
                        <select value={formData.transaction_type} onChange={e => setFormData(p => ({ ...p, transaction_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="txn-type-select">
                          {txnTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div><label className="text-sm font-medium">Fund Currency</label>
                        <input type="text" readOnly value={fundCurrency} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500" /></div>
                      <div><label className="text-sm font-medium">Amount Received</label>
                        <input type="number" value={formData.transaction_amount} onChange={e => setFormData(p => ({ ...p, transaction_amount: parseFloat(e.target.value) || 0 }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="txn-amount-input" />
                        <p className="text-[10px] text-gray-400 mt-0.5">For "Drawdown Called" rows, use the drawdown approval flow.</p>
                      </div>
                      <div><label className="text-sm font-medium">Class of Units</label>
                        <select value={formData.class_of_units || ''} onChange={e => {
                          const selectedClass = filteredClasses.find(c => c.name === e.target.value);
                          setFormData(p => ({ ...p, class_of_units: e.target.value, isin: selectedClass?.isin || p.isin || '' }));
                        }} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="txn-class-select">
                          <option value="">Select</option>
                          {filteredClasses.map(c => <option key={c.id || c.name} value={c.name}>{c.name} (FV: {c.face_value})</option>)}
                        </select>
                        {investmentClasses.length > 0 && filteredClasses.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">No classes available. Select classes in Investment Summary first.</p>
                        )}
                      </div>
                      <div><label className="text-sm font-medium">Units Issued</label>
                        <input type="text" readOnly value={computedUnits != null ? computedUnits.toFixed(4) : '-'} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500" data-testid="txn-units-display" /></div>
                      <div><label className="text-sm font-medium">ISIN</label>
                        <input type="text" value={formData.isin || ''} onChange={e => setFormData(p => ({ ...p, isin: e.target.value.toUpperCase() }))} placeholder="Auto-populated from class" className="w-full border rounded-lg px-3 py-2 text-sm mt-1 font-mono" data-testid="txn-isin-input" /></div>
                      <div className="flex items-center gap-2 pt-6">
                        <input type="checkbox" id="pledged-check" checked={formData.is_pledged || false} onChange={e => setFormData(p => ({ ...p, is_pledged: e.target.checked }))} className="rounded text-red-600" data-testid="txn-pledged-check" />
                        <label htmlFor="pledged-check" className="text-sm font-medium text-red-700">Pledged Units</label>
                      </div>
                      <div className="md:col-span-2"><label className="text-sm font-medium">Remarks</label>
                        <input type="text" value={formData.remarks || ''} onChange={e => setFormData(p => ({ ...p, remarks: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" /></div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={() => { setShowTxnForm(false); setEditingTxn(null); }} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
                      <button onClick={isEditing ? handleEditTransaction : handleCreateTransaction} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg" data-testid="save-txn-btn">
                        <Save size={14} className="mr-1 inline" /> {isEditing ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* ----- Drawdown Calls (rolled up) ----- */}
              <DrawdownCallsSection
                accountId={id}
                fundFilter={fundFilter}
                fmt={fmt}
                currentUser={user}
                onChange={loadData}
              />

              {filteredTxns.length === 0 ? <p className="text-gray-500 text-center py-6">No transactions yet</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                      {canDelete && (
                        <th className="px-3 py-2 w-10">
                          <input type="checkbox" checked={filteredTxns.length > 0 && filteredTxns.every(t => selectedTxns.has(t.id))}
                            onChange={() => toggleAllTxnSelect(filteredTxns)} className="h-4 w-4 text-blue-600 rounded" data-testid="txn-select-all" />
                        </th>
                      )}
                      <th className="px-3 py-2 cursor-pointer select-none" onClick={() => setTxnSortDir(d => d === 'asc' ? 'desc' : 'asc')} data-testid="txn-sort-date">
                        <span className="inline-flex items-center gap-1">Date {txnSortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                      </th>
                      <th className="px-3 py-2">Fund</th><th className="px-3 py-2">Currency</th><th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Class</th><th className="px-3 py-2">ISIN</th><th className="px-3 py-2 text-right">Units Issued</th><th className="px-3 py-2 text-center">Pledged</th><th className="px-3 py-2">Remarks</th><th className="px-3 py-2">Maker / Checker</th><th className="px-3 py-2 w-20"></th>
                    </tr></thead>
                    <tbody>
                      {filteredTxns.map(t => (
                        <tr key={t.id} className={`border-b hover:bg-gray-50 ${selectedTxns.has(t.id) ? 'bg-blue-50' : ''} ${t.is_pledged ? 'bg-red-50/40' : ''}`}>
                          {canDelete && (
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={selectedTxns.has(t.id)} onChange={() => toggleTxnSelect(t.id)}
                                className="h-4 w-4 text-blue-600 rounded" data-testid={`txn-select-${t.id}`} />
                            </td>
                          )}
                          <td className="px-3 py-2">{fmtDate(t.date_of_transaction)}</td>
                          <td className="px-3 py-2">{t.fund_name}</td>
                          <td className="px-3 py-2 text-gray-500">{t.currency || '-'}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{t.transaction_type}</span></td>
                          <td className="px-3 py-2 text-right font-medium">{fmt(t.amount_received != null && t.amount_received !== 0 ? t.amount_received : t.transaction_amount)}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{t.class_of_units || '-'}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs font-mono">{t.isin || '-'}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{t.units_issued != null ? Number(t.units_issued).toFixed(4) : '-'}</td>
                          <td className="px-3 py-2 text-center">{t.is_pledged ? <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">PLEDGED</span> : <span className="text-gray-300 text-xs">Free</span>}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{t.remarks || '-'}</td>
                          <td className="px-3 py-2 text-xs">
                            {(t.created_by || t.approved_by) ? (
                              <div className="flex flex-col gap-0.5" data-testid={`audit-${t.id}`}>
                                {t.created_by && (
                                  <div className="flex items-center gap-1" title={t.created_by.email || ''}>
                                    <span className="text-[10px] uppercase tracking-wider text-gray-400 w-12">Maker</span>
                                    <span className="text-gray-700 truncate max-w-[120px]">{t.created_by.name}</span>
                                  </div>
                                )}
                                {t.approved_by && (
                                  <div className="flex items-center gap-1" title={t.approved_by.email || ''}>
                                    <span className="text-[10px] uppercase tracking-wider text-green-500 w-12">Checker</span>
                                    <span className="text-gray-700 truncate max-w-[120px]">{t.approved_by.name}</span>
                                  </div>
                                )}
                              </div>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              {canEdit && <button onClick={() => { setEditingTxn({ ...t }); setShowTxnForm(false); }} className="p-1 text-gray-400 hover:text-blue-600" data-testid={`edit-txn-${t.id}`}><Edit size={14} /></button>}
                              {canDelete && <button onClick={() => handleDeleteTransaction(t.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* NAV Tab */}
          {activeTab === 'navs' && (
            <div data-testid="tab-content-navs">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">Net Asset Value</h3>
                  <select value={fundFilter} onChange={e => setFundFilter(e.target.value)} className="border rounded-lg px-2 py-1 text-sm">
                    <option value="">All Funds</option>{investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <>
                      <button onClick={() => setShowBulkNav(true)} className="inline-flex items-center px-3 py-2 border border-blue-600 text-blue-600 rounded-lg text-sm hover:bg-blue-50" data-testid="bulk-upload-nav-btn">
                        <Upload size={16} className="mr-1" /> Bulk Upload
                      </button>
                      <button onClick={() => setShowNavForm(true)} className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-nav-btn">
                        <Plus size={16} className="mr-1" /> Add NAV
                      </button>
                    </>
                  )}
                </div>
              </div>

              {showNavForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="nav-form">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div><label className="text-sm font-medium">Fund *</label>
                      <select value={navForm.fund_id} onChange={e => setNavForm(p => ({ ...p, fund_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="nav-fund-select">
                        <option value="">Select</option>{investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select></div>
                    <div><label className="text-sm font-medium">Fund Currency</label>
                      <input type="text" readOnly value={navForm.fund_id ? (getFund(navForm.fund_id)?.fund_currency || '-') : ''} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500" /></div>
                    <div><label className="text-sm font-medium">Date *</label>
                      <input type="date" value={navForm.date_of_nav} onChange={e => setNavForm(p => ({ ...p, date_of_nav: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="nav-date-input" /></div>
                    <div><label className="text-sm font-medium">NAV Value</label>
                      <input type="number" value={navForm.nav_value} onChange={e => setNavForm(p => ({ ...p, nav_value: parseFloat(e.target.value) || 0 }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="nav-value-input" /></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button onClick={() => setShowNavForm(false)} className="px-3 py-1.5 text-sm border rounded-lg">Cancel</button>
                    <button onClick={handleCreateNAV} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg" data-testid="save-nav-btn"><Save size={14} className="mr-1 inline" /> Save</button>
                  </div>
                </div>
              )}

              {filteredNavs.length === 0 ? <p className="text-gray-500 text-center py-6">No NAV records yet</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2">Date</th><th className="px-3 py-2">Fund</th><th className="px-3 py-2">Currency</th><th className="px-3 py-2 text-right">NAV Value</th><th className="px-3 py-2 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {filteredNavs.map(n => {
                        const fund = getFund(n.fund_id);
                        const classNavs = Array.isArray(n.class_navs) ? n.class_navs : [];
                        const hasOverride = classNavs.some(c => c.overridden);
                        const isExpanded = expandedNavId === n.id;
                        return (
                          <React.Fragment key={n.id}>
                            <tr className="border-b hover:bg-gray-50">
                              <td className="px-3 py-2">
                                {classNavs.length > 0 && (
                                  <button
                                    onClick={() => setExpandedNavId(isExpanded ? null : n.id)}
                                    className="text-gray-500 hover:text-gray-900"
                                    data-testid={`nav-expand-${n.id}`}
                                  >
                                    {isExpanded ? '▾' : '▸'}
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2">{fmtDate(n.date_of_nav)}</td>
                              <td className="px-3 py-2">
                                {n.fund_name}
                                {hasOverride && (
                                  <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800 rounded font-medium" title="One or more classes overridden — Fund NAV cascade won't overwrite this row">
                                    Overridden
                                  </span>
                                )}
                                {n.source_fund_nav_id && !hasOverride && (
                                  <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded" title="Auto-cascaded from Fund NAV">
                                    From Fund NAV
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-500">{fund?.fund_currency || '-'}</td>
                              <td className="px-3 py-2 text-right font-medium">{fmt(n.nav_value)}</td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex justify-end gap-1">
                                  {canEdit && (
                                    <button
                                      onClick={() => openOverrideNav(n)}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Override NAV (class-wise)"
                                      data-testid={`nav-override-${n.id}`}
                                    >
                                      <Edit size={14} />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button onClick={() => handleDeleteNAV(n.id)} className="p-1 text-gray-400 hover:text-red-500" title="Delete">
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {isExpanded && classNavs.length > 0 && (
                              <tr className="bg-gray-50 border-b">
                                <td></td>
                                <td colSpan={5} className="px-3 py-3">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-gray-600">
                                        <th className="px-2 py-1 text-left">Class</th>
                                        <th className="px-2 py-1 text-right">Units</th>
                                        <th className="px-2 py-1 text-right">NAV / Unit</th>
                                        <th className="px-2 py-1 text-right">Total Value</th>
                                        <th className="px-2 py-1 text-center">Override</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {classNavs.map((c, idx) => (
                                        <tr key={`${n.id}-${c.class_name}-${idx}`} className="border-t">
                                          <td className="px-2 py-1">{c.class_name}</td>
                                          <td className="px-2 py-1 text-right">{fmt(c.units)}</td>
                                          <td className="px-2 py-1 text-right">{fmt(c.nav_per_unit)}</td>
                                          <td className="px-2 py-1 text-right font-medium">{fmt(c.total_value)}</td>
                                          <td className="px-2 py-1 text-center">
                                            {c.overridden ? (
                                              <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800 rounded">Yes</span>
                                            ) : (
                                              <span className="text-gray-400 text-[10px]">No</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Override NAV Modal (Phase N) */}
              {editingNav && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="nav-override-modal">
                  <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="px-6 py-4 border-b flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold">Override NAV — {editingNav.fund_name}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Date: {editingNav.date_of_nav}</p>
                      </div>
                      <button onClick={closeOverrideNav} className="text-gray-500 hover:text-gray-700">×</button>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded mb-4">
                        Overriding a class marks it so future Fund NAV cascades will NOT overwrite these values.
                      </p>
                      <div className="overflow-x-auto border rounded">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium">Class</th>
                              <th className="px-3 py-2 text-right font-medium">Units</th>
                              <th className="px-3 py-2 text-right font-medium">NAV / Unit</th>
                              <th className="px-3 py-2 text-right font-medium">Total Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {navOverrideClasses.map((c, idx) => {
                              const tv = Number(c.units || 0) * Number(c.nav_per_unit || 0);
                              return (
                                <tr key={`${c.class_name}-${idx}`} className="border-b last:border-0">
                                  <td className="px-3 py-2 font-medium">{c.class_name}</td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      value={c.units}
                                      onChange={e => updateOverrideClass(idx, 'units', e.target.value)}
                                      className="w-full px-2 py-1 border rounded text-right"
                                      data-testid={`nav-override-units-${idx}`}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      value={c.nav_per_unit}
                                      onChange={e => updateOverrideClass(idx, 'nav_per_unit', e.target.value)}
                                      className="w-full px-2 py-1 border rounded text-right"
                                      data-testid={`nav-override-navpu-${idx}`}
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium">{fmt(tv)}</td>
                                </tr>
                              );
                            })}
                            <tr className="bg-gray-50 font-semibold">
                              <td className="px-3 py-2" colSpan={3}>Total</td>
                              <td className="px-3 py-2 text-right">
                                {fmt(navOverrideClasses.reduce((s, c) => s + Number(c.units || 0) * Number(c.nav_per_unit || 0), 0))}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2">
                      <button onClick={closeOverrideNav} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100">Cancel</button>
                      <button
                        onClick={handleSaveOverrideNav}
                        disabled={navOverrideSaving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                        data-testid="nav-override-save"
                      >
                        {navOverrideSaving ? 'Saving…' : 'Save Override'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Bank & Demat Tab */}
          {activeTab === 'bank_demat' && (
            <BankDematTab accountId={id} canEdit={canEdit} canDelete={canDelete} accountPan={account.pan} />
          )}

          {/* Nomination Tab — Individual-only. Guarded here in case the
              user changed type_of_investor after landing on this tab. */}
          {activeTab === 'nomination' && isIndividual && (
            <NominationTab accountId={id} canEdit={canEdit} canDelete={canDelete} />
          )}

          {/* Compliance Tab — per-account event-based rule toggles. */}
          {activeTab === 'compliance' && (
            <ComplianceTab accountId={id} />
          )}

          {/* History Tab (Admin only) */}
          {activeTab === 'history' && isAdmin && (
            <div data-testid="tab-content-history">
              <EntityAuditHistory entityType="investor_account" entityId={id} />
            </div>
          )}


          {/* Documentation Tab */}
          {activeTab === 'docs' && (
            <div data-testid="tab-content-docs">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Documentation</h3>
                {canEdit && (
                  <button onClick={() => setShowNewSection(true)} className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-section-btn">
                    <FolderPlus size={16} className="mr-1" /> Add Section
                  </button>
                )}
              </div>

              {showNewSection && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-end gap-3" data-testid="new-section-form">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700">Section Name</label>
                    <input type="text" value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="e.g. Contribution Agreements, KYC Documents" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="section-name-input"
                      onKeyDown={e => e.key === 'Enter' && handleCreateSection()} autoFocus />
                  </div>
                  <button onClick={() => setShowNewSection(false)} className="px-3 py-2 text-sm border rounded-lg text-gray-600">Cancel</button>
                  <button onClick={handleCreateSection} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg" data-testid="save-section-btn"><Save size={14} className="mr-1 inline" /> Save</button>
                </div>
              )}

              {docSections.length === 0 && !showNewSection ? (
                <div className="text-center py-12 text-gray-400">
                  <FileText size={40} className="mx-auto mb-3 opacity-50" />
                  <p>No document sections yet. Create sections like "KYC Documents", "Contribution Agreements" to organize your documents.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {docSections.map(section => (
                    <div key={section.id} className="border rounded-lg overflow-hidden" data-testid={`doc-section-${section.id}`}>
                      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}>
                        <div className="flex items-center gap-2">
                          {expandedSections[section.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <h4 className="font-semibold text-gray-800">{section.name}</h4>
                          <span className="text-xs text-gray-400 ml-2">{(section.items || []).length} document(s)</span>
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          {canEdit && (
                            <button onClick={() => setShowNewItem(prev => ({ ...prev, [section.id]: true }))} className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1" data-testid={`add-item-${section.id}`}>
                              <Plus size={14} className="inline mr-1" />Add Document
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDeleteSection(section.id)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`delete-section-${section.id}`} title="Delete (Admin only)">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      {expandedSections[section.id] && (
                        <div className="px-4 py-3">
                          {showNewItem[section.id] && (
                            <div className="flex items-end gap-3 mb-3 bg-blue-50 p-3 rounded-lg">
                              <div className="flex-1">
                                <label className="text-xs font-medium text-gray-600">Document Name</label>
                                <input type="text" value={newItemName[section.id] || ''} onChange={e => setNewItemName(prev => ({ ...prev, [section.id]: e.target.value }))}
                                  placeholder="e.g. ID Proof, Address Proof" className="w-full border rounded px-3 py-1.5 text-sm mt-1" data-testid={`item-name-${section.id}`}
                                  onKeyDown={e => e.key === 'Enter' && handleCreateItem(section.id)} autoFocus />
                              </div>
                              <button onClick={() => setShowNewItem(prev => ({ ...prev, [section.id]: false }))} className="px-2 py-1.5 text-xs border rounded text-gray-600">Cancel</button>
                              <button onClick={() => handleCreateItem(section.id)} className="px-2 py-1.5 text-xs bg-blue-600 text-white rounded" data-testid={`save-item-${section.id}`}>Save</button>
                            </div>
                          )}
                          {(!section.items || section.items.length === 0) && !showNewItem[section.id] ? (
                            <p className="text-xs text-gray-400 py-2">No documents in this section</p>
                          ) : (
                            <div className="space-y-2">
                              {(section.items || []).map(item => (
                                <div key={item.id} className="flex items-center justify-between bg-white border rounded-lg px-4 py-3 hover:shadow-sm transition-shadow" data-testid={`doc-item-${item.id}`}>
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <FileText size={18} className="text-gray-400 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="font-medium text-sm text-gray-800 truncate">{item.name}</p>
                                      {item.file_name ? (
                                        <p className="text-xs text-green-600 truncate">
                                          {item.file_name} {item.file_size ? `(${fmtSize(item.file_size)})` : ''}
                                        </p>
                                      ) : (
                                        <p className="text-xs text-gray-400">No file uploaded</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                    {canEdit && (
                                      <>
                                        <input type="file" ref={el => fileInputRefs.current[item.id] = el} className="hidden"
                                          onChange={e => { if (e.target.files[0]) handleUpload(section.id, item.id, e.target.files[0]); }} />
                                        <button onClick={() => fileInputRefs.current[item.id]?.click()} disabled={uploading[item.id]}
                                          className="inline-flex items-center text-xs px-2.5 py-1.5 border rounded text-blue-600 hover:bg-blue-50 disabled:opacity-50" data-testid={`upload-${item.id}`}>
                                          <Upload size={12} className="mr-1" /> {uploading[item.id] ? 'Uploading...' : item.file_name ? 'Replace' : 'Upload'}
                                        </button>
                                      </>
                                    )}
                                    {item.file_name && (
                                      <button onClick={() => handleDownload(item.id)} className="inline-flex items-center text-xs px-2.5 py-1.5 border rounded text-green-600 hover:bg-green-50" data-testid={`download-${item.id}`}>
                                        <Download size={12} className="mr-1" /> Download
                                      </button>
                                    )}
                                    {canDelete && (
                                      <button onClick={() => handleDeleteItem(section.id, item.id)} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-item-${item.id}`}>
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bulk Upload Modals */}
      <BulkUploadModal
        isOpen={showBulkTxn}
        onClose={() => setShowBulkTxn(false)}
        templateColumns={['Fund', 'Date', 'Type', 'Amount', 'Class of Units', 'Drawdown Number', 'Remarks']}
        templateData={txnTemplateData}
        onUpload={handleBulkTxnUpload}
        entityName="Transactions"
        filename="Transactions"
        instructions={(
          <>
            <strong>Tip:</strong> For rows where <code className="px-1 bg-blue-100 rounded">Type</code> is{' '}
            <code className="px-1 bg-blue-100 rounded">Drawdown Received</code>, the{' '}
            <code className="px-1 bg-blue-100 rounded">Drawdown Number</code> column is{' '}
            <strong>required</strong> — these rows are saved as <strong>pending-approval receipt tranches</strong>{' '}
            on the matching drawdown and become ledger transactions only after a maker-checker approves them.
            All other types (<code className="px-1 bg-blue-100 rounded">Commitment Agreed</code>,{' '}
            <code className="px-1 bg-blue-100 rounded">Distribution</code>, etc.) post directly to the ledger.
          </>
        )}
      />
      <BulkUploadModal
        isOpen={showBulkNav}
        onClose={() => setShowBulkNav(false)}
        templateColumns={['Fund', 'Date', 'NAV Value']}
        templateData={navTemplateData}
        onUpload={handleBulkNavUpload}
        entityName="NAV Records"
        filename="NAV"
      />

      {/* Pledged Units Warning Popup */}
      {showPledgedPopup && pledgedSummary?.has_pledged && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="pledged-popup">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 border-l-4 border-red-500">
            <div className="flex items-start gap-3">
              <div className="bg-red-100 rounded-full p-2 flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">Pledged Units Detected</h3>
                <p className="text-sm text-gray-600 mb-3">This investor account has units that are currently <strong className="text-red-600">pledged</strong>.</p>
                <div className="bg-red-50 rounded-lg p-3 text-xs space-y-1 mb-4">
                  <div className="flex justify-between"><span className="text-gray-600">Pledged transactions:</span><span className="font-semibold">{pledgedSummary.pledged_count}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Pledged units:</span><span className="font-semibold">{fmt(pledgedSummary.pledged_units)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Free units:</span><span className="font-semibold">{fmt(pledgedSummary.free_units)}</span></div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={() => { setShowPledgedPopup(false); setActiveTab('transactions'); }} className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50" data-testid="pledged-popup-view-btn">View Transactions</button>
              <button onClick={() => setShowPledgedPopup(false)} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700" data-testid="pledged-popup-close-btn">Acknowledge</button>
            </div>
          </div>
        </div>
      )}

      {/* SOA Generation Modal */}
      {showSOAModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="soa-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Generate Statement of Account</h3>
              <button onClick={() => setShowSOAModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Fund</label>
                <select value={soaFundId} onChange={(e) => handleSOAFundChange(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="soa-fund-select">
                  <option value="">Choose a fund...</option>
                  {investments.map(inv => (
                    <option key={inv.fund_id} value={inv.fund_id}>{inv.fund_name || inv.fund_id}</option>
                  ))}
                </select>
              </div>
              {soaFundId && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">SOA Template</label>
                    {soaTemplates.length === 0 ? (
                      <p className="text-sm text-amber-600">No SOA templates found. Create one in Fund Details &gt; Document Templates.</p>
                    ) : (
                      <select value={soaTemplateId} onChange={(e) => setSOATemplateId(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="soa-template-modal-select">
                        <option value="">Choose a template...</option>
                        {soaTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">From Date</label>
                      <select value={soaFromDate} onChange={(e) => setSOAFromDate(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-modal-from-date">
                        <option value="since_inception">Since Inception</option>
                        <option value="_custom">Custom Date</option>
                      </select>
                      {soaFromDate === '_custom' && (
                        <input type="date" onChange={(e) => setSOAFromDate(e.target.value)} className="w-full mt-1 px-2 py-1.5 border rounded text-sm" />
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">To Date</label>
                      <input type="date" value={soaToDate} onChange={(e) => setSOAToDate(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-modal-to-date" />
                      <p className="text-xs text-gray-400 mt-0.5">Empty = today</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">NAV</label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="radio" name="soaNav" checked={soaIncludeNav} onChange={() => setSOAIncludeNav(true)} className="text-blue-600" /> With NAV
                      </label>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="radio" name="soaNav" checked={!soaIncludeNav} onChange={() => setSOAIncludeNav(false)} className="text-blue-600" /> Without NAV
                      </label>
                    </div>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowSOAModal(false)} className="px-4 py-2 text-gray-600 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleGenerateSOA} disabled={soaGenerating || !soaFundId || !soaTemplateId} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50" data-testid="soa-generate-confirm-btn">
                  {soaGenerating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {soaGenerating ? 'Generating...' : 'Generate & Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer History modal — timeline of committed transfers */}
      {historyModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={() => setHistoryModal(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="transfer-history-modal">
            <div className="flex justify-between items-start p-5 border-b sticky top-0 bg-white z-10">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <History size={16} className="text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Transfer History</h3>
                </div>
                <p className="text-xs text-gray-500">{historyModal.fundName}</p>
              </div>
              <button onClick={() => setHistoryModal(null)} className="p-1 text-gray-400 hover:text-gray-600" data-testid="transfer-history-close"><X size={20} /></button>
            </div>
            <div className="p-5">
              {historyLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 justify-center py-8">
                  <Loader2 size={16} className="animate-spin" /> Loading transfer history…
                </div>
              ) : historyItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8 italic">
                  No committed transfers on this fund yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {historyItems.map((item) => {
                    const isOut = item.direction === 'out';
                    return (
                      <div
                        key={item.transfer_id}
                        className={`border rounded-lg p-3 ${isOut ? 'border-red-200 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}
                        data-testid={`history-item-${item.transfer_id}`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <ArrowLeftRight size={14} className={isOut ? 'text-red-600' : 'text-emerald-600'} />
                            <span className={`text-xs font-bold uppercase tracking-wide ${isOut ? 'text-red-700' : 'text-emerald-700'}`}>
                              {isOut ? 'Transferred OUT' : 'Received IN'}
                            </span>
                            <span className="text-xs text-gray-500 font-mono">{item.transfer_reference}</span>
                          </div>
                          <span className="text-xs text-gray-600 font-semibold">{item.agreement_date || '—'}</span>
                        </div>
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900">
                            {isOut ? 'To' : 'From'}: {item.counterparty_name || '—'}
                            {item.counterparty_folio_no ? <span className="text-gray-500 font-normal ml-1">({item.counterparty_folio_no})</span> : null}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            Class {item.class_name} · Commitment {' '}
                            <span className="font-mono font-semibold">{Number(item.commitment_amount_transferred || 0).toLocaleString()}</span>
                            {' '}· Contribution {' '}
                            <span className="font-mono">{Number(item.contribution_amount_transferred || 0).toLocaleString()}</span>
                          </div>
                          {item.reason && (
                            <div className="text-[11px] text-gray-500 mt-1 italic">
                              {item.reason}{item.reason_note ? ` — ${item.reason_note}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper components
const Info = ({ label, value }) => (<div><span className="text-sm text-gray-500">{label}</span><p className="mt-1 text-gray-900 font-medium">{value || '-'}</p></div>);

const PersonList = ({ title, list }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
    {(!list || list.length === 0) ? <p className="text-xs text-gray-400">None</p> : (
      <div className="space-y-2">
        {list.map((p, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-3 border space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div><span className="text-gray-500 text-xs">Name</span><p className="font-medium">{p.name}</p></div>
              <div><span className="text-gray-500 text-xs">Email</span><p>{p.email || '-'}</p></div>
              <div><span className="text-gray-500 text-xs">Mobile</span><p data-testid={`account-pc-mobile-display`}>{
                (p.mobile_country_code && p.mobile)
                  ? `${p.mobile_country_code} ${p.mobile}`
                  : (p.mobile || '-')
              }</p></div>
              <div><span className="text-gray-500 text-xs">Telephone</span><p>{p.telephone || '-'}</p></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div><span className="text-gray-500 text-xs">Authorised Signatory</span><p>{p.authorised_signatory === 'Yes' ? 'Yes' : 'No'}</p></div>
              {p.authorised_signatory === 'Yes' && (
                <div><span className="text-gray-500 text-xs">Position / Title</span><p>{p.signatory_position || '-'}</p></div>
              )}
              {p.remarks && (
                <div className="col-span-2"><span className="text-gray-500 text-xs">Remarks</span><p>{p.remarks}</p></div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AddrView = ({ title, addr }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
    {!addr || !addr.address ? <p className="text-xs text-gray-400">Not provided</p> : (
      <div className="bg-gray-50 rounded-lg p-3 border text-sm space-y-1">
        <p>{addr.address}</p>
        <p>{[addr.city, addr.state, addr.country, addr.zip_code].filter(Boolean).join(', ')}</p>
        {addr.remarks && <p className="text-gray-500 mt-1"><span className="text-xs text-gray-400">Remarks: </span>{addr.remarks}</p>}
      </div>
    )}
  </div>
);

export default InvestorAccountDetails;
